import {gql} from '@apollo/client';

import {Sequence} from 'components/Sequence';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import Protein from 'molbio/aa-sequences/AaSequence';
import {
  AssemblyHistoryQuery,
  AssemblyHistoryQueryVariables,
} from 'molbio/visual-history/assembly-history/__generated/AssemblyHistoryQuery';
import AssemblyHistoryTabState from 'molbio/visual-history/assembly-history/AssemblyHistoryTabState';
import {VISUAL_HISTORY_EVENT_LIST_ITEM_FRAGMENT} from 'molbio/visual-history/VisualHistoryEventListItem';
import {VISUAL_HISTORY_EVENT_METADATA_FIELD_FRAGMENT} from 'molbio/visual-history/VisualHistoryEventMetadataField';
import {
  VISUAL_HISTORY_EVENT_SOURCE_INFO_FRAGMENT,
  VISUAL_HISTORY_POLYMER_MAP_FRAGMENT,
} from 'molbio/visual-history/VisualHistoryEventSource';
import APIUtil from 'util/APIUtil';

export const ASSEMBLY_HISTORY_TAB_CONTENTS_QUERY = gql`
  query AssemblyHistoryQuery($entityId: StrictString!) @engComponent(name: MOLBIO) @skipAudit {
    assemblyHistory(entityId: $entityId) {
      ... on VisualHistoryEventNode {
        ...VisualHistoryEventListItemFragment
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${VISUAL_HISTORY_EVENT_LIST_ITEM_FRAGMENT}
  ${VISUAL_HISTORY_EVENT_METADATA_FIELD_FRAGMENT}
  ${VISUAL_HISTORY_EVENT_SOURCE_INFO_FRAGMENT}
  ${VISUAL_HISTORY_POLYMER_MAP_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Queries assembly history information for simple polymers.
 */
export async function fetchAssemblyHistory(fileId: string): Promise<AssemblyHistoryQuery | null> {
  if (APIUtil.isNucleotideSequence(fileId) || APIUtil.isProtein(fileId)) {
    return await doQuerySafe<AssemblyHistoryQuery, AssemblyHistoryQueryVariables>(
      {query: ASSEMBLY_HISTORY_TAB_CONTENTS_QUERY},
      {variables: {entityId: fileId}}
    );
  }
  return null;
}

/**
 * Returns an assembly history tab state for the given polymer if there is valid assembly information to show.
 */
export function createAssemblyHistoryTabState<Polymer extends Sequence | Protein>(
  polymer: Polymer,
  data?: AssemblyHistoryQuery | null
): AssemblyHistoryTabState<Polymer> | null {
  if (data?.assemblyHistory != null && !isInaccessible(data.assemblyHistory)) {
    return new AssemblyHistoryTabState({polymer});
  }
  return null;
}
